import { useFormContext } from 'react-hook-form'
import { toNumber } from 'lodash-es'
import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import { Box, Stack, Tooltip, Typography } from '@mui/material'
import { AddIconButton } from '@ui/actions'
import VerticalTransmitterTargetList from './VerticalTransmitterTargetList'

const fmt2 = new Intl.NumberFormat('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const formatRelativePosition = (relative_position: string) => {
  return fmt2.format(toNumber(relative_position))
}

interface Props {
  supportWithTargets: ElementSupportItemWithTargets
  elementLength: number
  selectedSupportGuid: string
  selectedTargetGuid: string
  handleDeleteTarget: (
    event: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    targetGuid: string,
  ) => void
  handleClickSupport: () => void
  handleAddTarget: () => void
  allowAddTarget: boolean
  allowDelete: boolean
}

export const TransmitterSupportWithTargets = ({
  supportWithTargets,
  elementLength,
  selectedSupportGuid,
  selectedTargetGuid,
  handleClickSupport,
  handleDeleteTarget,
  handleAddTarget,
  allowAddTarget,
  allowDelete,
}: Props) => {
  const { setValue } = useFormContext()
  const { guid: supportGuid, targets, relative_interval, relative_position } = supportWithTargets

  const hasRelativePosition = relative_position !== undefined
  const hasRelativeInterval = relative_interval !== undefined

  const transmitterPointPosition =
    elementLength && hasRelativePosition ? toNumber(relative_position) * elementLength : undefined
  const transmitterIntervalPosition =
    elementLength && hasRelativeInterval
      ? {
          lower: toNumber(relative_interval?.lower) * elementLength,
          upper: toNumber(relative_interval?.upper) * elementLength,
        }
      : undefined

  const absTransmitterPositionFormatted = hasRelativePosition
    ? `${fmt2.format(transmitterPointPosition ?? 0)}m`
    : hasRelativeInterval
    ? `${fmt2.format(transmitterIntervalPosition?.lower ?? 0)}m - ${fmt2.format(
        transmitterIntervalPosition?.upper ?? 0,
      )}m`
    : '-'

  const relativePositionFormatted = hasRelativePosition
    ? formatRelativePosition(relative_position)
    : undefined
  const relativeIntervalFormatted = hasRelativeInterval
    ? {
        lower: formatRelativePosition(relative_interval?.lower),
        upper: formatRelativePosition(relative_interval?.upper),
      }
    : undefined

  const isSelected = selectedSupportGuid === supportGuid

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      key={supportGuid}
      onClick={handleClickSupport}
    >
      <Box
        border={1}
        borderColor="grey.200"
        borderRadius={1}
        paddingX={1}
        bgcolor={isSelected ? 'grey.200' : 'transparent'}
      >
        <Stack direction="row" alignItems="center" spacing={1}>
          <Typography fontWeight={isSelected ? 'bold' : 'normal'} fontSize="small" noWrap>
            {'vert. Verb. @'}
          </Typography>
          <Stack direction="column" alignItems="center" spacing={0} padding={0}>
            <Typography fontWeight={isSelected ? 'bold' : 'normal'} fontSize="small">
              {`${absTransmitterPositionFormatted}`}
            </Typography>
            <Typography fontWeight={isSelected ? 'bold' : 'normal'} fontSize="small">
              {relativePositionFormatted && ` (${relativePositionFormatted})`}
              {relativeIntervalFormatted &&
                ` (${relativeIntervalFormatted?.lower} - ${relativeIntervalFormatted?.upper})`}
            </Typography>
          </Stack>
        </Stack>
      </Box>
      <ArrowRightAltIcon />
      <VerticalTransmitterTargetList
        key={targets.length}
        selectedTargetGuid={selectedTargetGuid}
        targets={targets}
        allowDelete={allowDelete}
        onClickTarget={targetGuid => {
          handleClickSupport()
          setValue('selectedTargetGuid', targetGuid)
        }}
        onClickDelete={handleDeleteTarget}
      />
      {allowAddTarget && (
        <Tooltip title="weiteres Ziel hinzufügen">
          <span>
            <AddIconButton
              onClick={handleAddTarget}
              color="primary"
              aria-label="weiteres Ziel hinzufügen"
            />
          </span>
        </Tooltip>
      )}
    </Stack>
  )
}
